import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import {
    CreatePurchaseRequest,
    IPatientPurchase,
    IPurchaseDetail,
    SetSessionsUsedBeforeRequest,
} from "../library/models/patientPurchase";
import { BaseService } from "./baseService";

/**
 * What patients bought, and how much of it is left.
 *
 * Tenant-scoped like EvaluationsService -- which clinic comes from the token, never from
 * an argument.
 *
 * Several of these are factory methods taking an id rather than plain mutations, because
 * `BaseService.createMutation` bakes its URL in at construction time and these endpoints
 * are nested under a purchase. The delete is hand-rolled for a second reason: the helper
 * sends its variable as `?id=`, which is not the shape of
 * `/patient-purchases/{id}/sessions/{sessionId}`.
 */
export class PatientPurchasesService extends BaseService {
    /**
     * Staff get every purchase at the clinic, newest first. A patient calling this same
     * method gets their own instead -- the server tells the two apart from the caller's
     * role, the same way `AppointmentsService.getAppointments` does -- with the money and
     * any internal notes left out. This is what the Dashboard's "Active Plans" card calls.
     */
    getPurchases(): UseQueryOptions<IPatientPurchase[], Error> {
        return this.createGetQuery<IPatientPurchase[]>('/patient-purchases');
    }

    /** One patient's purchases. */
    getPurchasesForPatient(patientId: number): UseQueryOptions<IPatientPurchase[], Error> {
        return this.createGetQuery<IPatientPurchase[]>('/patient-purchases', { patientId });
    }

    /** One purchase and every appointment against it. */
    getPurchase(purchaseId: number): UseQueryOptions<IPurchaseDetail, Error> {
        return this.createGetQuery<IPurchaseDetail>(`/patient-purchases/${purchaseId}`);
    }

    /**
     * Sells the patient a service. The price the patient pays is worked out on the
     * server from the list price and the discount, so it is not part of the request.
     *
     * A sale does not settle work already delivered. Both deferred-billing flows -- a
     * converted free evaluation, a session paid for after it was given -- name the
     * purchase on the appointment instead: see `AppointmentsService.linkPurchaseMutation`.
     */
    createPurchaseMutation(): UseMutationOptions<IPatientPurchase, Error, CreatePurchaseRequest> {
        return this.createMutation<IPatientPurchase, CreatePurchaseRequest>('/patient-purchases');
    }

    // Recording a session is no longer an operation on a purchase. It is an appointment
    // reaching HELD -- see AppointmentsService.createAppointmentMutation for a walk-in and
    // updateStatusMutation for one that was booked. The remainder follows from those rows.

    /**
     * Records how many of a purchase's sessions were given before the clinic moved onto
     * Carefull. The whole count, not a change to it; 0 undoes it. Administrators only.
     */
    setSessionsUsedBeforeMutation(
        purchaseId: number
    ): UseMutationOptions<IPatientPurchase, Error, SetSessionsUsedBeforeRequest> {
        return {
            mutationFn: (request: SetSessionsUsedBeforeRequest): Promise<IPatientPurchase> =>
                this.put<IPatientPurchase>(
                    `/patient-purchases/${purchaseId}/sessions-used-before`,
                    request
                ),
        };
    }

    /**
     * Withdraws a purchase. Soft on the server: the record and its held appointments
     * survive, and anything still booked against it is cancelled with it.
     */
    cancelPurchaseMutation(): UseMutationOptions<void, Error, number> {
        return {
            mutationFn: (purchaseId: number): Promise<void> =>
                this.delete<void>(`/patient-purchases/${purchaseId}`),
        };
    }
}
